import { Button, Col, Container, Row } from "react-bootstrap"
import { useParams } from "react-router"
import { useGetBuildingByIdQuery, useGetPropertiesListQuery, useEditPropertyMutation } from "../../../services/bClientApi"
import BuildingEntry from "../../../Components/Buildings/BuildingEntry"
import DataLogList from "../../../Components/DataLogs/DataLogList"
import GenericResourceAccess from "../../../Components/ResourceAccess/GenericResourceAccess"
import { PropertyEntry } from "../../../Components/PropertyEntry/PropertyEntry"
import { PropertyType } from "../../../Types/propertyTypes"
import { SelectPropertyModal } from "../../../Components/Modals/SelectPropertyModal/selectPropertyModal"
import { ConfirmModal } from "../../../Components/Modals/confirmModal"
import { useState } from "react"
import { useAppDispatch } from "../../../redux/store"
import { showError, showSuccess } from "../../../redux/counter/counterSlice"
import Collapsible from "../../../Components/Collapsible/Collapsible"
import BuildingAmenities from "../../../Components/BuildingAmenity/BuildingAmenities/BuildingAmenities"

export default function BuildingPage() {
  const { buildingId = '' } = useParams()
  const dispatch = useAppDispatch()
  const [showModal, setShowModal] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [propertyToRemove, setPropertyToRemove] = useState<PropertyType | null>(null)

  const {
    data: buildingData,
    error,
  } = useGetBuildingByIdQuery(buildingId, { skip: !buildingId })

  const [editProperty] = useEditPropertyMutation()

  // get list of properties
  const {
    data: propertiesData,
    refetch: refetchProperties
  } = useGetPropertiesListQuery({
    propertiesQuery: {
      buildingId: buildingId
    },
    pagination: {
      page: 1,
      limit: 100
    }
  }, { skip: !buildingId })

  const propertiesList = propertiesData?.docs || []

  // Get list of property IDs already in this building for filtering
  const existingPropertyIds = propertiesList.map((p: PropertyType) => p._id)

  const handlePropertySelect = async (propertyData: PropertyType | PropertyType[]) => {
    const properties = Array.isArray(propertyData) ? propertyData : [propertyData]

    let successCount = 0
    let failCount = 0

    for (const property of properties) {
      try {
        await editProperty({
          propertyId: property._id,
          propertyInfo: {
            buildingId: buildingId
          }
        }).unwrap()
        successCount++
      } catch (err) {
        console.error('Failed to update property:', err)
        failCount++
      }
    }

    if (successCount > 0) {
      dispatch(showSuccess(`${successCount} propert${successCount === 1 ? 'y' : 'ies'} added to this building`))
      refetchProperties()
    }
    if (failCount > 0) {
      dispatch(showError(`Failed to add ${failCount} propert${failCount === 1 ? 'y' : 'ies'} to building`))
    }
  }

  const handleRemoveFromBuilding = async () => {
    if (!propertyToRemove) return

    try {
      await editProperty({
        propertyId: propertyToRemove._id,
        propertyInfo: {
          buildingId: ''
        }
      }).unwrap()

      dispatch(showSuccess(`Property "${propertyToRemove.title}" removed from this building`))
      setShowRemoveModal(false)
      setPropertyToRemove(null)
      refetchProperties()
    } catch (err) {
      console.error('Failed to remove property from building:', err)
      dispatch(showError('Failed to remove property from building'))
    }
  }

  return (
    <Container>
      <Row>
        <Col
          xs={12}
          lg={9}
        >
          <h1>Single Building Page</h1>
          <hr />
          {error && (<>{JSON.stringify(error)}<br /></>)}

          {buildingId && buildingData && (
            <div className="building-info">
              <BuildingEntry
                buildingInfo={buildingData}
                details={true}
                disableLinks={{
                  view: 1,
                }}
              />
              <br />
              {propertiesList && propertiesList.length > 0 ? (
                <Collapsible
                  title={`Properties in this Building (${propertiesList.length})`}
                  openText="Show Properties"
                  closeText="Hide Properties"
                >
                  <div className="building-properties-list">
                    {/* <h4>Properties in this Building</h4> */}
                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-2 mb-3"
                      onClick={() => setShowModal(true)}
                    >
                      Add Property to Building
                    </Button>
                    {propertiesList.map((propertyEntry: PropertyType, idx: number) => (
                      <PropertyEntry
                        key={idx}
                        propertyInfo={propertyEntry}
                        details={false}
                        extraButtons={[
                          {
                            label: 'Remove from Building',
                            variant: 'warning',
                            onClick: (property: PropertyType) => {
                              setPropertyToRemove(property)
                              setShowRemoveModal(true)
                            }
                          }
                        ]}
                      />
                    ))}
                  </div>
                </Collapsible>
              ) : (
                <div>
                  <p className="text-muted">No properties found for this building.</p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowModal(true)}
                  >
                    Add Property to Building
                  </Button>
                </div>
              )}

            </div>
          )}

          <hr />

          <Collapsible
            title="Amenities"
          >
            <BuildingAmenities buildingId={buildingId} />
          </Collapsible>

          <hr />

          <GenericResourceAccess resourceType="building" resourceId={buildingId} />

        </Col>

        <Col
          xs={12}
          lg={3}
        >
          <DataLogList
            dataType="building"
            dataId={buildingId ? buildingId : ''}
          />
        </Col>
      </Row>

      <SelectPropertyModal
        active={showModal}
        onClose={() => setShowModal(false)}
        onSelect={handlePropertySelect}
        title="Add Properties to Building"
        message="Select properties to add to this building"
        multiSelect={true}
        filterFn={(property: PropertyType) => !existingPropertyIds.includes(property._id)}
      />

      <ConfirmModal
        active={showRemoveModal}
        onClose={() => {
          setShowRemoveModal(false)
          setPropertyToRemove(null)
        }}
        onConfirm={handleRemoveFromBuilding}
        title="Remove Property from Building"
        message={`Are you sure you want to remove "${propertyToRemove?.title}" from this building?`}
      />
    </Container>
  )
}
